import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Dynamic key-value pairs controller for extra_values
// Provides a delightful UX for adding custom properties to assets and asset templates
export default class extends Controller {
  static targets = ["container", "template", "counter", "emptyState"]
  static values = {
    fieldName: { type: String, default: "asset[extra_values]" },
    maxPairs: { type: Number, default: 30 }
  }

  connect() {
    this.updateCounter()
    this.updateEmptyState()
  }

  add(event) {
    event.preventDefault()

    const currentCount = this.containerTarget.querySelectorAll('.extra-value-pair').length
    if (currentCount >= this.maxPairsValue) {
      this.showMaxLimitMessage()
      return
    }

    const template = this.templateTarget.content.cloneNode(true)
    const pairElement = template.querySelector('.extra-value-pair')
    
    // Generate unique ID for this pair
    const uniqueId = Date.now() + Math.random().toString(36).substr(2, 9)
    pairElement.dataset.pairId = uniqueId

    // Add enter animation
    pairElement.classList.add('animate-fade-in')

    this.containerTarget.appendChild(template)
    this.updateCounter()
    this.updateEmptyState()

    // Focus on the key input
    const keyInput = this.containerTarget.querySelector(`[data-pair-id="${uniqueId}"] input[data-field="key"]`)
    if (keyInput) {
      keyInput.focus()
    }
  }

  remove(event) {
    event.preventDefault()
    
    const pair = event.target.closest('.extra-value-pair')
    if (pair) {
      // Add exit animation
      pair.classList.add('animate-fade-out')
      
      setTimeout(() => {
        pair.remove()
        this.updateCounter()
        this.updateEmptyState()
      }, 200)
    }
  }

  updateKey(event) {
    const pair = event.target.closest('.extra-value-pair')
    const hiddenInput = pair.querySelector('input[type="hidden"][data-role="value-input"]')
    const valueInput = pair.querySelector('input[data-field="value"]')
    const key = this.sanitizeKey(event.target.value)
    
    // Update the hidden input name to use the sanitized key
    if (hiddenInput && key) {
      hiddenInput.name = `${this.fieldNameValue}[${key}]`
      hiddenInput.value = valueInput ? valueInput.value : ''
    }
  }

  updateValue(event) {
    const pair = event.target.closest('.extra-value-pair')
    const hiddenInput = pair.querySelector('input[type="hidden"][data-role="value-input"]')
    const keyInput = pair.querySelector('input[data-field="key"]')
    const key = keyInput ? this.sanitizeKey(keyInput.value) : ''
    
    // Update the hidden input with the new value
    if (hiddenInput && key) {
      hiddenInput.name = `${this.fieldNameValue}[${key}]`
      hiddenInput.value = event.target.value
    }
  }

  sanitizeKey(key) {
    // Convert to a safe key format (alphanumeric and underscores)
    return key.toString()
      .toLowerCase()
      .replace(/[^a-z0-9_\s-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 100)
  }

  updateCounter() {
    if (!this.hasCounterTarget) return
    
    const count = this.containerTarget.querySelectorAll('.extra-value-pair').length
    this.counterTarget.textContent = `${count}/${this.maxPairsValue}`
    
    // Update color based on count
    if (count >= this.maxPairsValue) {
      this.counterTarget.classList.remove('text-[#8eb3cc]', 'text-yellow-400')
      this.counterTarget.classList.add('text-red-400')
    } else if (count >= this.maxPairsValue * 0.8) {
      this.counterTarget.classList.remove('text-[#8eb3cc]', 'text-red-400')
      this.counterTarget.classList.add('text-yellow-400')
    } else {
      this.counterTarget.classList.remove('text-yellow-400', 'text-red-400')
      this.counterTarget.classList.add('text-[#8eb3cc]')
    }
  }

  updateEmptyState() {
    if (!this.hasEmptyStateTarget) return
    
    const count = this.containerTarget.querySelectorAll('.extra-value-pair').length
    if (count === 0) {
      this.emptyStateTarget.classList.remove('hidden')
    } else {
      this.emptyStateTarget.classList.add('hidden')
    }
  }

  showMaxLimitMessage() {
    // Create and show a temporary toast message
    const toast = document.createElement('div')
    toast.className = 'fixed bottom-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in flex items-center gap-2'
    toast.innerHTML = `
      <span class="material-symbols-outlined">warning</span>
      <span>${t("javascript.controllers.extra_values_controller.max_pairs_allowed", { max: this.maxPairsValue })}</span>
    `
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.classList.add('animate-fade-out')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
